/**
 * 会话相关工具函数
 */

const GUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * 判断 selectedKeys 是否为历史会话 ID。
 * Conversation.ID 来自后端 Guid；菜单 key 或其它页面状态不能作为会话 ID 使用。
 */
export function isHistoryConversation(selectedKeys: string): boolean {
  return typeof selectedKeys === "string" && GUID_PATTERN.test(selectedKeys);
}

/**
 * 引用字段归一化。
 * 三种来源统一成下游展示代码使用的 v1 内部形状（references_*），并保留原始字段：
 *  - 上游 PikeRAG V3 原生 `event: references`：documentId/segmentId/segment/position/documentName/sectionId
 *  - 新版内联 ```references 围栏：documentId/chunkId/excerpt/filename/heading（见 splitReferencesFence）
 *  - 历史记录（DB Quote）：已是 v1 形状（references_document_id/...）
 * 使流式、内联块与历史回放共用同一套渲染逻辑。
 */
export function normalizeReferences(list: any): any[] {
  if (!Array.isArray(list)) return [];
  return list.map((r) => {
    if (!r || typeof r !== "object") return r;
    return {
      ...r,
      references_document_id: r.references_document_id ?? r.documentId ?? r.document_id ?? "",
      references_segment_id:
        r.references_segment_id ?? r.segmentId ?? r.chunkId ?? r.segment_id ?? "",
      references_segment: r.references_segment ?? r.segment ?? r.excerpt ?? "",
      references_segment_position:
        r.references_segment_position ?? r.position ?? r.segment_position ?? 0,
      references_document_name:
        r.references_document_name ?? r.documentName ?? r.filename ?? r.document_name ?? "",
      references_section_id: r.references_section_id ?? r.sectionId ?? r.section_id ?? "",
      related_document_ids: getReferenceRelatedDocIds(r),
    };
  });
}

/**
 * 「内联块建表，事件补元数据」：以内联 ```references 解析出的引用为权威清单与顺序，
 * 用后续 `event: references` 携带的更全字段（segment 正文、position、relatedDocumentIds）补全。
 * 匹配优先 documentId|segmentId，长度一致时回退按序号（两边都是 [1]..[N] 的有序清单）对齐。
 * inline 为空时回退为 event（兼容旧版仅有 event 的服务端）。两个入参都应已 normalizeReferences。
 */
export function mergeReferenceEventMetadata(inline: any[], event: any[]): any[] {
  if (!Array.isArray(inline) || inline.length === 0) return Array.isArray(event) ? event : [];
  if (!Array.isArray(event) || event.length === 0) return inline;

  const keyOf = (r: any) =>
    `${String(r?.references_document_id ?? "").trim()}|${String(r?.references_segment_id ?? "").trim()}`;
  const byKey = new Map<string, any>();
  event.forEach((e) => {
    const k = keyOf(e);
    if (k !== "|") byKey.set(k, e);
  });
  const sameLen = inline.length === event.length;

  return inline.map((r, i) => {
    let ev = byKey.get(keyOf(r));
    if (!ev && sameLen) ev = event[i];
    if (!ev) return r;
    const mergedRelated = Array.from(
      new Set(
        [...(r.related_document_ids || []), ...getReferenceRelatedDocIds(ev)]
          .map((x) => String(x ?? "").trim())
          .filter(Boolean),
      ),
    );
    return {
      ...r,
      // segment 正文优先用事件的全文（内联 excerpt 是带省略号的摘录）
      references_segment: ev.references_segment || r.references_segment,
      references_segment_id: r.references_segment_id || ev.references_segment_id,
      references_segment_position: r.references_segment_position || ev.references_segment_position,
      references_section_id: r.references_section_id || ev.references_section_id,
      related_document_ids: mergedRelated,
    };
  });
}

/**
 * 取一条引用关联的全部文档 ID（relatedDocumentIds，始终含 owner 自身）。
 * 内容入湖去重后，同内容文档只有 owner 真正向量化，命中的 documentId 即 owner；
 * relatedDocumentIds 把 owner 关联回调用方实际持有/上传的重复件 id。
 * 上游 V3 references 用 camelCase relatedDocumentIds；历史落库（Quote）用 references_related_document_ids；
 * 两者都拿不到时回退到该引用自身的 documentId。
 */
export function getReferenceRelatedDocIds(ref: any): string[] {
  if (!ref || typeof ref !== "object") return [];
  const raw =
    ref.related_document_ids ?? ref.relatedDocumentIds ?? ref.references_related_document_ids;
  const ids = Array.isArray(raw) ? raw.map((x: any) => String(x ?? "").trim()).filter(Boolean) : [];
  const own = String(ref.references_document_id ?? ref.documentId ?? ref.document_id ?? "").trim();
  if (own && !ids.includes(own)) ids.push(own);
  return ids;
}

/** buildDocumentsFromReferences 产出的文档行（右侧 References 区按文档去重后的清单） */
export interface BuiltDocument {
  id?: string;
  name: string;
  citations: string;
  canDownload: boolean;
  /** 该文档对应的引用序号（即正文 [N] 角标，按 references 顺序 1-based） */
  segmentNums: number[];
  /** 该文档关联的全部文档 ID（含 owner 与重复件），用于后续 getdocumentid 命中 DB */
  relatedIds: string[];
}

/**
 * 把 normalize 后的 references 按文档去重，构建右侧 References 区的文档行清单
 * （不含 getdocumentid 补全的大小/下载/guid——那些后补）。
 * 供流式途中逐条预览与最终态共用，保证“边构建边显示”与最终结果一致。
 */
export function buildDocumentsFromReferences(references: any[]): BuiltDocument[] {
  const docMap = new Map<string, BuiltDocument>();
  (references || []).forEach((ref: any, idx: number) => {
    const segmentNum = idx + 1;
    const id = ref?.references_document_id ?? ref?.document_id ?? "";
    const refName = (ref?.references_document_name ?? id ?? "").toString().trim();
    if (!id && !refName) return;
    const key = id || refName;
    const relatedIds = getReferenceRelatedDocIds(ref);

    const rawSegment = (ref?.references_segment ?? "").toString();
    let seg = rawSegment;
    const marker = "Content: ";
    const markerIndex = rawSegment.indexOf(marker);
    if (markerIndex >= 0) seg = rawSegment.slice(markerIndex + marker.length);

    const existing = docMap.get(key);
    if (!existing) {
      docMap.set(key, {
        id,
        name: refName || id,
        citations: seg || "",
        canDownload: true,
        segmentNums: [segmentNum],
        relatedIds,
      });
      return;
    }
    existing.segmentNums.push(segmentNum);
    relatedIds.forEach((rid) => {
      if (!existing.relatedIds.includes(rid)) existing.relatedIds.push(rid);
    });
    if (seg) {
      existing.citations = existing.citations ? `${existing.citations}\n\n---\n\n${seg}` : seg;
    }
  });
  return Array.from(docMap.values());
}

/**
 * getdocumentid 返回项里取 DocumentGuid，作为右侧文档清单的校准身份：
 * 命中 DB 的引用会把它写入 doc.guidName，右侧清单的去重、勾选 Only use selected sources
 * 后下次提问带上的 references.*.references_document_id，均以这个 DocumentGuid 为准。
 */
export function getDocInfoDocumentGuid(info: any): string {
  return String(info?.DocumentGuid ?? info?.documentGuid ?? "").trim();
}

/** 取文档来源标识（Teams Channel URL / SharePoint 站点地址等），供右侧清单展示来源。 */
export function getDocInfoDataSource(info: any): string {
  return String(info?.DataSource ?? info?.dataSource ?? "").trim();
}

/** 取文档所属的 General / Study Source 主键，供右侧勾选与检索范围联动。 */
export function getDocInfoSourcePathAskId(info: any): string {
  return String(
    info?.SourcePathAskId ?? info?.sourcePathAskId ?? info?.DocumentList_SourcePathAskId ?? "",
  ).trim();
}

const normalizeDocumentCategoryIds = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value
      .map((id) => (typeof id === "string" || typeof id === "number" ? String(id).trim() : ""))
      .filter(Boolean);
  }
  if (typeof value !== "string" && typeof value !== "number") return [];

  const text = String(value).trim();
  if (!text) return [];
  if (text.startsWith("[")) {
    try {
      return normalizeDocumentCategoryIds(JSON.parse(text));
    } catch {
      return [];
    }
  }
  return [text];
};

/** 取 General 文档所属分类（SourceType ∪ DocCategoryIds），供 Category 与右侧勾选联动。 */
export function getDocInfoCategoryIds(info: any): string[] {
  const ids = [
    ...normalizeDocumentCategoryIds(info?.SourcePathAskSourceType ?? info?.sourcePathAskSourceType),
    ...normalizeDocumentCategoryIds(
      info?.SourcePathAskDocCategoryIds ?? info?.sourcePathAskDocCategoryIds,
    ),
    ...normalizeDocumentCategoryIds(info?.docCategoryIds),
  ];
  return Array.from(new Set(ids.map((id) => id.toLowerCase())));
}

/**
 * getdocumentid 返回项是否为「有效（未软删）」文档。
 * 后端为支持历史引用下载，已忽略 IsValid 过滤一并返回软删旧行（IsValid=false）；
 * 右侧会话文档清单只展示 IsValid=true 的文档，据此决定是否打 inDocumentList 标记。
 * 软删行仍保留在 documents（正文角标 / References 区 / 下载链接需要），只是不进右侧边栏。
 * 仅当 IsValid 显式为 false 时判为软删；字段缺失时按有效处理，保持既有行为。
 */
export function isDocInfoValid(info: any): boolean {
  const v = info?.IsValid ?? info?.isValid;
  return !(v === false || v === "false");
}

/**
 * 在 getdocumentid 返回的文档列表中，按一条引用的 relatedDocumentIds 命中 DB 文档。
 * 命中规则：返回项的 DocumentGuid 落在 relatedIds 内（含 owner 与全部重复件）；
 * 未命中时按文件名回退匹配。
 */
export function matchDocInfoByRelatedIds(
  docInfoList: any[],
  relatedIds: string[],
  fallbackName?: string,
): any | undefined {
  const idSet = new Set(
    (relatedIds || []).map((x) => String(x).trim().toLowerCase()).filter(Boolean),
  );
  let matched =
    idSet.size > 0
      ? (docInfoList || []).find((info) => {
          const g = String(info?.DocumentGuid ?? info?.documentGuid ?? "")
            .trim()
            .toLowerCase();
          return g && idSet.has(g);
        })
      : undefined;
  const nm = (fallbackName ?? "").trim();
  if (!matched && nm) {
    matched = (docInfoList || []).find((info) => {
      const n =
        info?.FileName ??
        info?.fileName ??
        info?.documentName ??
        info?.DocumentName ??
        info?.name ??
        "";
      return typeof n === "string" && n.trim() === nm;
    });
  }
  return matched;
}
